import React from "react";
import { ReduxUpdater } from "../../../redux/ReduxUpdater";
import { ArrowRight, X } from "lucide-react";
import FileUpload, { IFileEntry } from "./Forms/FileUpload";
import { AnimatePresence, motion } from "framer-motion";
import TextInput from "./Forms/TextInput";
import SelectInput, { ISelectData } from "./Forms/SelectInput";
import { ApiCalls } from "../../../ApiCalls";
import { toast } from "react-toastify";
import { GlobalConstants } from "../../../GlobalConstants";
import { useSelector } from "react-redux";
import { uploadReceiptState } from "../../../redux/slices/receiptUploadSlice";
import { selectedUserState } from "../../../redux/slices/userSlice";
import { GlobalVariables } from "../../../GlobalVariables";

interface ReceiptUploadProps {
    className?: string;
}

export const ReceiptUpload: React.FC<ReceiptUploadProps> = ({
    className = undefined,
}) => {
    const currentSelectedUserState = useSelector(selectedUserState);
    const receiptData = useSelector(uploadReceiptState);

    const [isProcessing, setIsProcessing] = React.useState(false);
    const [files, setFiles] = React.useState<IFileEntry[]>([]);
    const [progress, setProgress] = React.useState(0);
    const [tmr, setTmr] = React.useState<NodeJS.Timer | null>(null);

    React.useMemo(() => {
        if (isProcessing) {
            setProgress(0);
            setTmr(setInterval(() => {
                setProgress(prevProgress => {
                    if (prevProgress >= 100) {
                        return 100;
                    }
                    return prevProgress + 1;
                });
            }, 333));
        }
        setProgress(0);
    }, [isProcessing]);

    React.useEffect(() => {
        if (!tmr) {
            return;
        }
        if (progress >= 100) {
            clearInterval(tmr);
        }
    }, [progress, tmr])
    
    const handleCancel = () => {
        ReduxUpdater.currentDialog = null;
    };

    const handleUpload = () => {
        if (currentSelectedUserState && receiptData.type && files.length > 0) {
            setIsProcessing(true);
            var receiptType = '';
            if (receiptData.type === '1') {
                receiptType = 'petty_cash';
            }
            if (receiptData.type === '2') {
                receiptType = 'reimbursement';
            }

            var F = files[0].file;
            if (GlobalVariables.fileBlob) {
                F = new File(
                    [GlobalVariables.fileBlob],
                    files[0].file.name,
                    {
                        type: files[0].file.type,
                        lastModified: Date.now()
                    }
                );
            }

            ApiCalls.uploadReceipt(
                currentSelectedUserState?.id,
                F,
                receiptType,
                receiptData.description
            ).then(res => {
                if (res?.error) {
                    toast.error(res.error);
                    setIsProcessing(false);
                    return;
                }
                if (res?.duplicate_of_receipt_id) {
                    ReduxUpdater.currentDialog = null;
                    toast.error('Duplicated receipt!');
                    return;
                }
                ReduxUpdater.uploadedReceiptID = res?.receipt_id ?? -1;
            }).catch(err => toast.error(err.message));
        }
    };

    const receiptOptions = React.useMemo(() => {
        const options: ISelectData[] = [
            {
                label: 'Choose type...',
                value: '-1',
                disabled: true,
            }
        ];

        if (ReduxUpdater.authenticatedUser?.user_role === GlobalConstants.UserRole.ADMIN) {
            if (
                ReduxUpdater.authenticatedUser.id === currentSelectedUserState?.id && 
                ReduxUpdater.authenticatedUser.card_name !== null &&
                ReduxUpdater.authenticatedUser.debit_card_number &&
                ReduxUpdater.authenticatedUser.debit_card_number.length === 5 &&
                !ReduxUpdater.authenticatedUser.debit_card_number.includes('X')
            ) {
                options.push({
                    label: 'I\'m not sure',
                    value: '3',
                });
                options.push({
                    label: 'Company Card',
                    value: '0',
                });
            }

            if (
                ReduxUpdater.authenticatedUser.id === currentSelectedUserState?.id
            ) {
                options.push({
                    label: 'Boat cash',
                    value: '1',
                });
            }
        }

        const selectedUser = ReduxUpdater.yachtCrewCards.find(o => o.id === currentSelectedUserState?.id);
        if (selectedUser && ReduxUpdater.authenticatedUser?.user_role === GlobalConstants.UserRole.ROOT) {
            if (
                selectedUser.card_name !== null &&
                selectedUser.debit_card_number &&
                selectedUser.debit_card_number.length === 5 &&
                !selectedUser.debit_card_number.includes('X')
            ) {
                options.push({
                    label: 'I\'m not sure',
                    value: '3',
                });
                options.push({
                    label: 'Company Card',
                    value: '0',
                });
                options.push({
                    label: 'Boat cash',
                    value: '1',
                });
            }
        }

        if (!(
            ReduxUpdater.authenticatedUser?.user_role === GlobalConstants.UserRole.ROOT &&
            ReduxUpdater.authenticatedUser?.id === currentSelectedUserState?.id
        )) {
            options.push({
                label: 'Reimbursement',
                value: '2',
            });
        }

        return options;
    }, [currentSelectedUserState]);

    React.useMemo(() => {
        if (receiptOptions.length === 2 && receiptData.type !== receiptOptions[1].value) {
            ReduxUpdater.receiptUpload = {...receiptData, type: receiptOptions[1].value as '-1' | '0' | '1' | '2' | '3'};
        } 
    }, [receiptData, receiptOptions]);

    return (
        <div className="h-full max-h-[-webkit-fill-available] w-full max-w-xl mx-auto p-4">
            <div className="max-h-[-webkit-fill-available] bg-white rounded-2xl shadow-sm border border-neutral-200">
                <AnimatePresence mode="wait">
                    <motion.div key="step1" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.2 }} className="p-3 sm:p-4">
                        <div className="space-y-6 overflow-y-auto">
                            <div className="grid grid-cols-1 overflow-y-auto gap-4">
                                <h2 className="text-xl font-semibold mb-1">Upload Receipt for {currentSelectedUserState?.first_name} {currentSelectedUserState?.last_name}</h2>
                                <FileUpload
                                    accept="image/*,application/pdf"
                                    multiple={false}
                                    maxSizeMB={5}
                                    onFilesChanged={setFiles}
                                    progress={isProcessing ? progress : 0}
                                />

                                {files.length > 0 && currentSelectedUserState && !isProcessing && (<>
                                    <SelectInput
                                        idx="type"
                                        label="Type"
                                        type={receiptOptions.length === 2 ? 'locked' : (receiptData.type === undefined ? 'empty' : 'edit')}
                                        classes="col-span-1"
                                        value={receiptData.type}
                                        options={receiptOptions}
                                        onChange={(newValue) => {
                                            if (receiptData.type !== newValue) {
                                                console.log('receiptUpload.type', receiptData.type, newValue);
                                                ReduxUpdater.receiptUpload = {...receiptData, type: newValue as '-1' | '0' | '1' | '2'};
                                            }
                                        }}
                                    />

                                    <TextInput
                                        idx="description"
                                        label="Description"
                                        type={receiptData.description === '' ? 'empty' : 'edit'}
                                        value={receiptData.description}
                                        onChange={(newValue) => {
                                            if (receiptData.description !== newValue) {
                                                ReduxUpdater.receiptUpload = {...receiptData, description: newValue};
                                            }
                                        }}
                                    />
                                </>)}
                            </div>
                        </div>
                        {!isProcessing && <div className="mt-4 flex justify-between">
                            <button onClick={handleCancel} className={`w-40 h-10 px-3 bg-buttons-invert rounded-lg flex justify-center items-center gap-2.5 disabled:opacity-50 disabled:pointer-events-none`}>
                                <div className="flex-1 text-center justify-start text-text-dark text-sm font-semibold font-['Inter'] leading-tight">Cancel <X className="mr-2 w-4 h-4 inline" /></div>
                            </button>
                            <button
                                className={`w-40 h-10 px-3 bg-buttons-primary rounded-lg flex justify-center items-center gap-2.5 disabled:opacity-50 disabled:pointer-events-none`}
                                disabled={files.length === 0 || !receiptData.description || !receiptData.type}
                                onClick={handleUpload}
                            >
                                <div className="flex-1 text-center justify-start text-text-dark-invert text-sm font-semibold font-['Inter'] leading-tight">Upload receipt <ArrowRight className="ml-2 w-4 h-4 inline" /></div>
                            </button>
                        </div>}
                    </motion.div>
                </AnimatePresence>
            </div>
        </div>
    );
};
